import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TablePager } from '@/components/ui';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

export interface AccountsPaginationProps {
    page: number;
    totalPages: number;
    pageSize: number;
    onPageSizeChange: (size: number) => void;
    rangeFrom: number;
    rangeTo: number;
    total: number;
    onGoTo: (page: number) => void;
    pageSizeOptions?: number[];
}

/**
 * EAA-1820: the ONE pager, replacing the realm's `accounts/AccountsPagination.tsx`
 * (paginates an already-scanned, client-filtered set) and the org's
 * `hierarchy/scopedAccounts/ScopedAccountsPagination.tsx` (paginates real server
 * `page`/`page_size` params). Both now drive real server pages (see
 * `useRealmAccountsAdapter`), so the two are the same component with the same
 * inputs — `rangeFrom`/`rangeTo`/`total` are computed by each adapter from
 * whatever it actually knows server-side.
 *
 * EAA-1839: no longer hand-rolled. The comment that used to sit here claimed the
 * kit's `TablePager` "only models prev/current/next — no first/last and no
 * numbered page-window — so it cannot cover this shape". That stopped being true
 * in EAA-1804, when `pages`/`onGoTo`/`onFirst`/`onLast` were added to the kit
 * naming THIS component as the reason; the stale comment kept a second copy of
 * the footer alive. EAA-1839 also moved the page-size picker into the kit
 * (`pageSize`), which is what genuinely was still missing — and with it the
 * Select's width floor, which had been living in app CSS as a forbidden
 * descendant selector into the kit's internals (`.ant-select`).
 *
 * What stays here: the copy, the arithmetic, and the page WINDOW (current ±2 plus
 * first/last) — the kit deliberately does not compute the window, see its
 * docstring.
 */
export function AccountsPagination({
    page,
    totalPages,
    pageSize,
    onPageSizeChange,
    rangeFrom,
    rangeTo,
    total,
    onGoTo,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
}: AccountsPaginationProps) {
    const { t } = useTranslation();

    // Small page-count window: current ±2, always showing first/last.
    const pageList = useMemo(() => {
        const window = new Set<number>([
            1,
            totalPages,
            page - 2,
            page - 1,
            page,
            page + 1,
            page + 2,
        ]);
        return [...window].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    }, [page, totalPages]);

    const labels = useMemo(
        () => ({
            prev: '‹',
            next: '›',
            page: `${page}/${totalPages}`,
            first: t('console.accounts.pagination.firstPageAriaLabel'),
            last: t('console.accounts.pagination.lastPageAriaLabel'),
            prevAria: t('console.accounts.pagination.previousPageAriaLabel'),
            nextAria: t('console.accounts.pagination.nextPageAriaLabel'),
        }),
        [t, page, totalPages]
    );

    return (
        <TablePager
            className="portal-console-pagination portal-accounts-pagination"
            page={page}
            totalPages={totalPages}
            onPrev={() => onGoTo(page - 1)}
            onNext={() => onGoTo(page + 1)}
            onFirst={() => onGoTo(1)}
            onLast={() => onGoTo(totalPages)}
            pages={pageList}
            onGoTo={onGoTo}
            labels={labels}
            summary={t('console.accounts.pagination.summary', {
                from: rangeFrom,
                to: rangeTo,
                total: total.toLocaleString('vi-VN'),
            })}
            pageSize={{
                value: pageSize,
                options: pageSizeOptions,
                onChange: onPageSizeChange,
                label: t('console.accounts.pagination.pageSizeLabel'),
            }}
        />
    );
}

export default AccountsPagination;
